import {
  AuthActions,
  Selectors,
  getState,
  widgetDispatch,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {PayloadAction} from '@reduxjs/toolkit';
import {all, call, put, takeEvery, takeLatest} from 'redux-saga/effects';

import {
  GetAccountAffiliateDetailsOpts,
  GetAccountAffiliateDetailsParams,
  GetAccountAffiliateMappingsParams,
  InitiateExchangeCurrencyOpts,
  InitiateExchangeCurrencyParams,
  InitiateMatchTierOpts,
  InitiateMatchTierParams,
  MatchTierResultCode,
  getAccountAffiliateDetails,
  getAccountAffiliateMappings,
  initiateExchangeCurrency,
  initiateMatchTier,
} from 'services/api/affiliate';

import {AffiliateAccountDetails, AffiliateActions} from './affiliate.reducer';

export function* handleGetAffiliateAccountList() {
  try {
    // TODO: This was commented out since the patrontoken is not yet implemented
    // on the LeapIntegration service. We will need to switch this back once it is.
    // Ref: https://caesarsdevelopment.atlassian.net/browse/ECOMM-1171

    // You can use the code below. This needs to be replaced in all the sagas in this file.
    // let sectoken;

    // if (Platform.OS === 'web') {
    //   sectoken = yield call(getState, Selectors.sectokenSelector);
    // } else {
    //   sectoken = yield call(getState, Selectors.patrontokenSelector);
    // }

    const sectoken = yield call(getState, Selectors.sectokenSelector);

    const data: GetAccountAffiliateMappingsParams = {sectoken};

    const response = yield call(getAccountAffiliateMappings, data);

    if (response?.data?.header?.resultStatus !== 'SUCCESS') {
      throw new Error();
    }

    yield put(
      AffiliateActions.getAffiliateAccountListSuccess(response.data.body)
    );
  } catch (e) {
    yield put(AffiliateActions.getAffiliateAccountListFailure());
  }
}

export function* handleInitiateExchangeCurrency(
  action: PayloadAction<InitiateExchangeCurrencyOpts>
) {
  try {
    const sectoken = yield call(getState, Selectors.sectokenSelector);

    const data: InitiateExchangeCurrencyParams = {sectoken, ...action.payload};

    const response = yield call(initiateExchangeCurrency, data);

    if (response?.data?.header?.resultStatus !== 'SUCCESS') {
      throw new Error(response?.data?.header?.resultMessage);
    }

    yield put(AffiliateActions.initiateExchangeCurrencySuccess());
    yield put(
      AffiliateActions.getAffiliateAccountDetails({
        affiliateaccount: action.payload.partneraccountid,
        affiliateid: action.payload.partnerid,
      })
    );
    yield call(
      widgetDispatch,
      AuthActions.setRewardsCredit(
        Number(response?.data?.body?.newRewardsBalance || 0)
      )
    );
  } catch (e: any) {
    yield put(
      AffiliateActions.initiateExchangeCurrencyFailure(
        e.message || 'Something went wrong'
      )
    );
  }
}

export function* handleGetAffiliateAccountDetails(
  action: PayloadAction<GetAccountAffiliateDetailsOpts>
) {
  try {
    const sectoken = yield call(getState, Selectors.sectokenSelector);
    const data: GetAccountAffiliateDetailsParams = {
      sectoken,
      ...action.payload,
    };

    const response = yield call(getAccountAffiliateDetails, data);

    if (response?.data?.header?.resultStatus !== 'SUCCESS') {
      throw new Error();
    }

    const accountDetails: AffiliateAccountDetails = {
      ...response?.data?.body,
      lastQueryTime: new Date(),
    };

    yield put(
      AffiliateActions.getAffiliateAccountDetailsSuccess(accountDetails)
    );
  } catch (e: any) {
    yield put(
      AffiliateActions.getAffiliateAccountDetailsFailure(
        action.payload.affiliateid
      )
    );
  }
}

export function* handleInitiateMatchTier(
  action: PayloadAction<InitiateMatchTierOpts>
) {
  try {
    const sectoken = yield call(getState, Selectors.sectokenSelector);

    const data: InitiateMatchTierParams = {
      sectoken,
      ...action.payload,
    };

    const response = yield call(initiateMatchTier, data);

    if (
      response?.data?.header?.resultStatus !== 'SUCCESS' &&
      response?.data?.header?.resultCode !== MatchTierResultCode.NOT_AVAILABLE // Note: This error code counts as success for this api call
    ) {
      yield put(
        AffiliateActions.initiateMatchTierFailure({
          resultCode: response?.data?.header?.resultCode,
          resultMessage: response?.data?.header?.resultMessage,
          partnerId: action.payload.partnerID,
        })
      );
      return;
    }
    yield put(AffiliateActions.getAffiliateAccountList());
    yield put(
      AffiliateActions.initiateMatchTierSuccess(action.payload.partnerID)
    );
  } catch (e) {
    yield put(
      AffiliateActions.initiateMatchTierFailure({
        resultCode: 'GENERAL',
        resultMessage: 'Server error',
        partnerId: action.payload.partnerID,
      })
    );
  }
}

export function* affiliateSagas() {
  yield all([
    takeLatest(
      AffiliateActions.getAffiliateAccountList,
      handleGetAffiliateAccountList
    ),
    takeLatest(
      AffiliateActions.initiateExchangeCurrency,
      handleInitiateExchangeCurrency
    ),
    takeEvery(
      AffiliateActions.getAffiliateAccountDetails,
      handleGetAffiliateAccountDetails
    ),
    takeEvery(AffiliateActions.initiateMatchTier, handleInitiateMatchTier),
  ]);
}
